import api from './client';
import type { ApiResponse } from '@engenharia/shared';

export type AnexoCategoria = 'diversos' | 'print_padrao_modulos' | 'foto_padrao_coletivo';

export interface AnexoGeral {
  id: number;
  cliente_id: number;
  categoria: AnexoCategoria;
  nome_original: string;
  mime_type: string | null;
  tamanho_bytes: number | null;
  descricao: string | null;
  enviado_por: string | null;
  created_at: string;
}

export interface AnexoObservacao {
  texto: string;
  atualizado_por: string | null;
  updated_at: string;
}

export async function uploadAnexos(
  clienteId: number,
  files: File[],
  opts?: { descricao?: string; enviadoPor?: string; categoria?: AnexoCategoria; onProgress?: (pct: number) => void }
): Promise<{ ids: number[] }> {
  const formData = new FormData();
  files.forEach((f) => formData.append('files', f));
  if (opts?.descricao) formData.append('descricao', opts.descricao);
  if (opts?.enviadoPor) formData.append('enviadoPor', opts.enviadoPor);
  if (opts?.categoria) formData.append('categoria', opts.categoria);

  const res = await api.post<ApiResponse<{ ids: number[] }>>(`/anexos/cliente/${clienteId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (e) => {
      if (e.total && opts?.onProgress) opts.onProgress(Math.round((e.loaded * 100) / e.total));
    },
  });
  return res.data.data!;
}

export async function listarAnexos(clienteId: number, busca?: string): Promise<AnexoGeral[]> {
  const res = await api.get<ApiResponse<AnexoGeral[]>>(`/anexos/cliente/${clienteId}`, {
    params: busca ? { busca } : {},
  });
  return res.data.data!;
}

export async function obterUrlAnexo(anexoId: number): Promise<string> {
  const res = await api.get<ApiResponse<{ url: string }>>(`/anexos/${anexoId}/url`);
  return res.data.data!.url;
}

// Stream via backend (mesma origem) — funciona em <img src> e pra download,
// diferente da URL assinada do MinIO (http:// interno, bloqueado por mixed content).
export function obterUrlStreamAnexo(anexoId: number, download = false): string {
  const apiKey = import.meta.env.VITE_API_KEY || 'Brunji1342Deguzaro@!#!@#';
  return `/api/anexos/${anexoId}/stream?key=${encodeURIComponent(apiKey)}${download ? '&download=1' : ''}`;
}

// ZIP com todos os anexos do cliente (ou só de uma categoria)
export function obterUrlZipAnexos(clienteId: number, categoria?: AnexoCategoria): string {
  const apiKey = import.meta.env.VITE_API_KEY || 'Brunji1342Deguzaro@!#!@#';
  return `/api/anexos/cliente/${clienteId}/zip?key=${encodeURIComponent(apiKey)}${categoria ? `&categoria=${categoria}` : ''}`;
}

export async function deletarAnexo(anexoId: number): Promise<void> {
  await api.delete(`/anexos/${anexoId}`);
}

export async function obterObservacao(clienteId: number): Promise<AnexoObservacao | null> {
  const res = await api.get<ApiResponse<AnexoObservacao | null>>(`/anexos/cliente/${clienteId}/observacao`);
  return res.data.data ?? null;
}

export async function salvarObservacao(
  clienteId: number,
  texto: string,
  atualizadoPor?: string
): Promise<AnexoObservacao | null> {
  const res = await api.put<ApiResponse<AnexoObservacao | null>>(`/anexos/cliente/${clienteId}/observacao`, {
    texto,
    atualizadoPor,
  });
  return res.data.data ?? null;
}
